import { Dialect } from '../dialect.js';
import { tokenize } from '../lexer/lexer.js';
import { normalizeKeywordText } from '../lexer/keywords.js';
import { parseFragment } from '../parser/parser.js';
import type { Expression, PseudoglossaProgram, Statement } from '../parser/ast.js';

/**
 * Αντίστροφη κατεύθυνση από το `natural-language.ts`: κείμενο σε "φυσική
 * γλώσσα κατά βήματα" → δομημένο AST (Ψευδογλώσσα). ΔΕΝ είναι γενικός parser
 * φυσικής γλώσσας (αδύνατο ντετερμινιστικά) — αναγνωρίζει ΜΟΝΟ τα ΓΝΩΣΤΑ,
 * ακριβή σχήματα GOTO του Κεφαλαίου 7 (ΑΝ/ΑΝ-ΑΛΛΙΩΣ/ΑΝ-ΑΛΛΙΩΣ_ΑΝ/ΟΣΟ/
 * ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ/ΓΙΑ — τα ίδια που παράγει το `printProgramAsNaturalLanguageSteps`)
 * και ΑΡΝΕΙΤΑΙ (δεν μαντεύει) οτιδήποτε άλλο: άγνωστη γραμμή, GOTO που δεν
 * ταιριάζει σε κανένα από τα 6 σχήματα, ελλείπον Βήμα, κ.λπ.
 *
 * Στρατηγική: (1) κάθε γραμμή "Βήμα Ν: ..." γίνεται ένα απλό IR-αντικείμενο
 * (plain εντολή / goto / υπό συνθήκη goto με 1 ή 2 στόχους) — οι ενσωματωμένες
 * εκφράσεις/εντολές ΞΑΝΑΧΡΗΣΙΜΟΠΟΙΟΥΝ τον υπάρχοντα lexer/parser (π.χ. "Θέσε
 * Χ = Υ" ξαναγράφεται σε "Χ ← Υ" και περνάει από το κανονικό `parseFragment`)
 * αντί να γράφεται νέος expression parser από την αρχή. (2) Μια αναδρομική
 * αναγνώριση σχημάτων ξαναχτίζει το δέντρο, χρησιμοποιώντας τους ΗΔΗ
 * γνωστούς στόχους GOTO για να υπολογίσει τα όρια κάθε δομής — όχι μαντεψιά.
 */

// --- Βήμα 1: γραμμή κειμένου → IR --------------------------------------------

interface PlainStep {
  readonly kind: 'plain';
  readonly statement: Statement;
}
interface GotoStep {
  readonly kind: 'goto';
  readonly target: number;
}
interface CondGoto1Step {
  readonly kind: 'condgoto1';
  readonly condition: Expression;
  readonly trueTarget: number;
}
interface CondGoto2Step {
  readonly kind: 'condgoto2';
  readonly condition: Expression;
  readonly trueTarget: number;
  readonly falseTarget: number;
}
type IRStep = PlainStep | GotoStep | CondGoto1Step | CondGoto2Step;

function parseExprText(text: string): Expression | null {
  const { tokens, errors } = tokenize(`__φγ__ ← ${text}`, Dialect.PSEUDOGLOSSA);
  if (errors.length > 0) return null;
  const { statements, error } = parseFragment(tokens, Dialect.PSEUDOGLOSSA);
  if (error !== null || statements === null || statements.length !== 1) return null;
  const stmt = statements[0]!;
  if (stmt.kind !== 'AssignmentStatement') return null;
  return stmt.value;
}

/**
 * §14.1, "Παρατηρήσεις": οι εντολές Διάβασε/Γράψε/Εμφάνισε/Εκτύπωσε
 * επιτρέπονται "με περισσότερη ελευθερία" — π.χ. "Διάβασε τον Χ και τον Υ"
 * ή "Διάβασε τον αριθμό Χ" αντί για το αυστηρό "Διάβασε Χ, Υ". Αφαιρεί τα
 * άρθρα (το/τα/τον/τη/την/του/της/τους/τις, ως ξεχωριστές λέξεις — όχι μέσα
 * σε άλλες) ΚΑΙ μια μικρή, ρητή λίστα περιγραφικών ουσιαστικών που συχνά
 * συνοδεύουν το άρθρο (π.χ. "τον ΑΡΙΘΜΟ Χ") — ΔΕΝ μαντεύουμε οποιοδήποτε
 * ουσιαστικό, μόνο αυτά ρητά στη λίστα (βλ. `IGNORABLE_READ_WRITE_WORDS`).
 * Μετατρέπει επίσης το "και" ανάμεσα σε ονόματα σε κόμμα — όλα ΜΟΝΟ για
 * αυτές τις εντολές (ΟΧΙ για Θέσε/συνθήκες, όπου το "και" είναι ο λογικός
 * τελεστής ΚΑΙ και πρέπει να μείνει όπως είναι, και τα άρθρα θα μπορούσαν
 * να συγκρουστούν με πραγματικές μεταβλητές μέσα σε εκφράσεις).
 */
// Όχι \b: το JS regex \b βασίζεται στο ASCII \w, που ΔΕΝ περιλαμβάνει ελληνικά γράμματα —
// ανάμεσα σε "Διάβασε" και τον επόμενο χαρακτήρα δεν υπάρχει ποτέ "όριο λέξης" να ανιχνευτεί.
// Κανονικοποιημένη μορφή (βλ. `execNormalized`) — αναγνωρίζει το ρήμα όποιο κι
// αν είναι το πεζά/κεφαλαία/τόνοι του μαθητή.
const READ_WRITE_VERB = /^(ΔΙΑΒΑΣΕ|ΓΡΑΨΕ|ΕΜΦΑΝΙΣΕ|ΕΚΤΥΠΩΣΕ)(?=\s|$)/;

/**
 * Λέξεις που αγνοούνται (αφαιρούνται) μέσα στα ορίσματα Διάβασε/Γράψε/
 * Εμφάνισε/Εκτύπωσε — άρθρα (κλειστή, πεπερασμένη γραμματική κλάση) ΚΑΙ μια
 * μικρή, ρητή λίστα περιγραφικών ουσιαστικών (π.χ. "αριθμό") που ζήτησε
 * ρητά ο χρήστης. ΔΕΝ γενικεύουμε σε "κάθε ουσιαστικό" — θα ήταν μάντεμα.
 */
const IGNORABLE_READ_WRITE_WORDS = ['το', 'τα', 'τον', 'τη', 'την', 'του', 'της', 'τους', 'τις', 'αριθμό'];

/**
 * Σαν `String.replace` με global regex, αλλά το `pattern` ταιριάζει πάνω στην
 * ΚΑΝΟΝΙΚΟΠΟΙΗΜΕΝΗ μορφή του κειμένου (βλ. `normalizeKeywordText`) — έτσι
 * "ΤΟ"/"το"/"Το" αναγνωρίζονται όλα το ίδιο. Η αντικατάσταση εφαρμόζεται στο
 * ΑΡΧΙΚΟ κείμενο (1-προς-1 αντιστοιχία θέσης χαρακτήρα, ίδιο σκεπτικό με το
 * `execNormalized`), ώστε ό,τι ΔΕΝ ταιριάζει (π.χ. ονόματα μεταβλητών) να
 * διατηρεί την πραγματική του γραφή.
 */
function replaceNormalizedGlobal(text: string, pattern: RegExp, replacer: (match: RegExpMatchArray) => string): string {
  const normalized = normalizeKeywordText(text);
  let result = '';
  let lastIndex = 0;
  for (const match of normalized.matchAll(pattern)) {
    const start = match.index!;
    result += text.slice(lastIndex, start) + replacer(match);
    lastIndex = start + match[0].length;
  }
  return result + text.slice(lastIndex);
}

function normalizeReadWriteArgs(text: string): string {
  if (!READ_WRITE_VERB.test(normalizeKeywordText(text))) return text;
  const ignorableAlternatives = IGNORABLE_READ_WRITE_WORDS.map((w) => normalizeKeywordText(w)).join('|');
  const ignorablePattern = new RegExp(`(^|[\\s,])(${ignorableAlternatives})(?=[\\s,]|$)`, 'g');
  const withoutIgnorableWords = replaceNormalizedGlobal(text, ignorablePattern, (m) => m[1] ?? '');
  const withCommas = replaceNormalizedGlobal(withoutIgnorableWords, /\s+ΚΑΙ\s+/g, () => ', ');
  return withCommas.replace(/\s{2,}/g, ' ').trim();
}

function parseLeafStatementText(text: string): Statement | null {
  const { tokens, errors } = tokenize(normalizeReadWriteArgs(text), Dialect.PSEUDOGLOSSA);
  if (errors.length > 0) return null;
  const { statements, error } = parseFragment(tokens, Dialect.PSEUDOGLOSSA);
  if (error !== null || statements === null || statements.length !== 1) return null;
  return statements[0]!;
}

interface ParsedHeader {
  readonly name: string;
  readonly inputVars: readonly string[];
  readonly outputVars: readonly string[];
  readonly steps: ReadonlyMap<number, IRStep>;
  readonly maxStep: number;
}

function splitVarList(text: string): readonly string[] {
  return text
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

// Οι λέξεις-σύνδεσμοι (ΑΝ/ΒΗΜΑ/ΤΟΤΕ/ΠΗΓΑΙΝΕ/ΣΤΟ/ΑΛΛΙΩΣ/ΘΕΣΕ) γράφονται εδώ σε
// ΚΑΝΟΝΙΚΟΠΟΙΗΜΕΝΗ μορφή (κεφαλαία, χωρίς τόνους — βλ. `normalizeKeywordText`)
// γιατί ταιριάζουν ΠΑΝΤΑ πάνω στην κανονικοποιημένη γραμμή, όχι στην αρχική
// (βλ. `execNormalized`) — έτσι "Τότε"/"τότε"/"ΤΟΤΕ"/"ΤΌΤΕ" αναγνωρίζονται
// όλα το ίδιο, ανεξάρτητα από το πώς έγραψε τα κεφαλαία/τόνους ο μαθητής.
const STEP_LINE = /^ΒΗΜΑ\s+(\d+)\s*:\s*(.*)$/d;
const CONDGOTO2_LINE = /^ΑΝ\s+(.+?)\s+ΤΟΤΕ\s+ΠΗΓΑΙΝΕ\s+ΣΤΟ\s+ΒΗΜΑ\s+(\d+)\s+ΑΛΛΙΩΣ\s+ΠΗΓΑΙΝΕ\s+ΣΤΟ\s+ΒΗΜΑ\s+(\d+)$/d;
const CONDGOTO1_LINE = /^ΑΝ\s+(.+?)\s+ΤΟΤΕ\s+ΠΗΓΑΙΝΕ\s+ΣΤΟ\s+ΒΗΜΑ\s+(\d+)$/d;
/** §14.1, "1ος τρόπος σύνταξης (Ισχύει μόνο στην ψευδογλώσσα)": "Αν συνθήκη τότε εντολή" σε ΜΙΑ γραμμή, χωρίς GOTO — π.χ. "Αν χ > 0 τότε Γράψε χ". Ελέγχεται ΜΕΤΑ τα CONDGOTO1/2 (πιο συγκεκριμένα, "πήγαινε στο Βήμα..."), ώστε να μην τα κρύψει. */
const INLINE_IF_LINE = /^ΑΝ\s+(.+?)\s+ΤΟΤΕ\s+(.+)$/d;
const GOTO_LINE = /^ΠΗΓΑΙΝΕ\s+ΣΤΟ\s+ΒΗΜΑ\s+(\d+)$/d;
// \s* (όχι \s+) γύρω από το "=": δέχεται και "Θέσε α=α+1" χωρίς κενά, όχι μόνο "Θέσε α = α+1".
const THESE_LINE = /^ΘΕΣΕ\s+(.+?)\s*=\s*(.+)$/d;

/**
 * Ταιριάζει ένα ΣΤΑΘΕΡΟ regex (λέξεις-σύνδεσμοι σε κανονικοποιημένη μορφή —
 * βλ. πιο πάνω) πάνω στην κανονικοποιημένη γραμμή, ώστε "Αν... Τότε..."
 * να αναγνωρίζεται όποιο κι αν είναι το πεζά/κεφαλαία ή οι τόνοι του
 * μαθητή (π.χ. αν τα κεφαλαιοποίησε όλα κατά λάθος). Οι ομάδες (capture
 * groups) εξάγονται όμως από την ΑΡΧΙΚΗ γραμμή (μέσω του 'd' flag, που δίνει
 * θέση/μήκος ανά ομάδα) — έτσι εκφράσεις/ονόματα μεταβλητών μέσα τους
 * διατηρούν την πραγματική τους γραφή. Ασφαλές γιατί το `normalizeKeywordText`
 * διατηρεί πάντα 1-προς-1 αντιστοιχία θέσης χαρακτήρα (κάθε χαρακτήρας της
 * αρχικής γραμμής αντιστοιχεί σε ΑΚΡΙΒΩΣ έναν χαρακτήρα της κανονικοποιημένης).
 */
function execNormalized(pattern: RegExp, line: string): RegExpExecArray | null {
  const match = pattern.exec(normalizeKeywordText(line));
  if (match === null) return null;
  const indices = (match as unknown as { indices?: ReadonlyArray<readonly [number, number] | undefined> }).indices;
  if (indices === undefined) return match;
  const result = [...match] as RegExpExecArray;
  for (let i = 1; i < match.length; i++) {
    const span = indices[i];
    if (span !== undefined) result[i] = line.slice(span[0], span[1]);
  }
  return result;
}

/** "Θέσε X = Y" -> "X ← Y" (ώστε να ξαναχρησιμοποιηθεί ο κανονικός parser) και αφαίρεση άρθρων/"και" σε Διάβασε/Γράψε/Εμφάνισε/Εκτύπωσε — κοινή λογική για τις κανονικές γραμμές ΚΑΙ για την ενσωματωμένη εντολή ενός "Αν...τότε <εντολή>". */
function textToLeafStatement(text: string): Statement | null {
  const thes = execNormalized(THESE_LINE, text);
  const leafText = thes !== null ? `${thes[1]} ← ${thes[2]}` : text;
  return parseLeafStatementText(leafText);
}

interface ParsedSteps {
  readonly steps: ReadonlyMap<number, IRStep>;
  readonly maxStep: number;
}

/**
 * Επεξεργάζεται ΜΟΝΟ γραμμές "Βήμα Ν: ..." — καμία γραμμή κεφαλίδας
 * (Αλγόριθμος/Είσοδος/Έξοδος) δεν αναμένεται εδώ. Χρησιμοποιείται και για
 * ολόκληρο πρόγραμμα (μετά την αφαίρεση της κεφαλίδας, βλ. `parseHeaderAndSteps`)
 * και για "Τμήμα αλγορίθμου" (απευθείας, βλ. `parseNaturalLanguageStepsToStatements`)
 * — ένα τμήμα δεν έχει καν κεφαλίδα.
 */
function parseStepLinesOnly(lines: readonly string[]): { readonly ok: true; readonly parsed: ParsedSteps } | { readonly ok: false; readonly reason: string } {
  const steps = new Map<number, IRStep>();
  let maxStep = 0;

  for (const line of lines) {
    const stepMatch = execNormalized(STEP_LINE, line);
    if (stepMatch === null) return { ok: false, reason: `Δεν αναγνωρίζεται η γραμμή: "${line}"` };
    const stepNum = Number(stepMatch[1]);
    const rest = stepMatch[2]!.trim();
    if (rest.length === 0) continue; // άδεια τελική ετικέτα "Βήμα Ν:" -- αγνοείται, δεν είναι πραγματικό βήμα
    if (steps.has(stepNum)) return { ok: false, reason: `Το Βήμα ${stepNum} εμφανίζεται περισσότερες από μία φορά.` };
    maxStep = Math.max(maxStep, stepNum);

    const c2 = execNormalized(CONDGOTO2_LINE, rest);
    if (c2 !== null) {
      const condition = parseExprText(c2[1]!);
      if (condition === null) return { ok: false, reason: `Μη έγκυρη συνθήκη στο Βήμα ${stepNum}.` };
      steps.set(stepNum, { kind: 'condgoto2', condition, trueTarget: Number(c2[2]), falseTarget: Number(c2[3]) });
      continue;
    }
    const c1 = execNormalized(CONDGOTO1_LINE, rest);
    if (c1 !== null) {
      const condition = parseExprText(c1[1]!);
      if (condition === null) return { ok: false, reason: `Μη έγκυρη συνθήκη στο Βήμα ${stepNum}.` };
      steps.set(stepNum, { kind: 'condgoto1', condition, trueTarget: Number(c1[2]) });
      continue;
    }
    const inlineIf = execNormalized(INLINE_IF_LINE, rest);
    if (inlineIf !== null) {
      const condition = parseExprText(inlineIf[1]!);
      if (condition === null) return { ok: false, reason: `Μη έγκυρη συνθήκη στο Βήμα ${stepNum}.` };
      const innerStatement = textToLeafStatement(inlineIf[2]!);
      if (innerStatement === null) return { ok: false, reason: `Μη έγκυρη εντολή στο Βήμα ${stepNum}: "${inlineIf[2]}"` };
      const ifStatement: Statement = { kind: 'IfStatement', condition, thenBranch: [innerStatement], elseIfBranches: [], elseBranch: null, endLine: null, ...POS };
      steps.set(stepNum, { kind: 'plain', statement: ifStatement });
      continue;
    }
    const g = execNormalized(GOTO_LINE, rest);
    if (g !== null) {
      steps.set(stepNum, { kind: 'goto', target: Number(g[1]) });
      continue;
    }
    const statement = textToLeafStatement(rest);
    if (statement === null) return { ok: false, reason: `Μη έγκυρη εντολή στο Βήμα ${stepNum}: "${rest}"` };
    steps.set(stepNum, { kind: 'plain', statement });
  }

  for (let i = 1; i <= maxStep; i++) {
    if (!steps.has(i)) return { ok: false, reason: `Λείπει το Βήμα ${i}.` };
  }
  return { ok: true, parsed: { steps, maxStep } };
}

function parseHeaderAndSteps(text: string): { readonly ok: true; readonly parsed: ParsedHeader } | { readonly ok: false; readonly reason: string } {
  const lines = text
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  let name: string | null = null;
  let sawInput = false;
  let sawOutput = false;
  const inputVars: string[] = [];
  const outputVars: string[] = [];
  const stepLines: string[] = [];

  for (const line of lines) {
    const algoMatch = execNormalized(/^ΑΛΓΟΡΙΘΜΟΣ\s+(.+)$/d, line);
    if (algoMatch !== null) {
      name = algoMatch[1]!.trim();
      continue;
    }
    const inputMatch = execNormalized(/^ΕΙΣΟΔΟΣ:\s*(.*)$/d, line);
    if (inputMatch !== null) {
      sawInput = true;
      inputVars.push(...splitVarList(inputMatch[1]!));
      continue;
    }
    const outputMatch = execNormalized(/^ΕΞΟΔΟΣ:\s*(.*)$/d, line);
    if (outputMatch !== null) {
      sawOutput = true;
      outputVars.push(...splitVarList(outputMatch[1]!));
      continue;
    }
    stepLines.push(line);
  }

  if (name === null) return { ok: false, reason: 'Λείπει η γραμμή "Αλγόριθμος <Όνομα>".' };
  // §14.1: σε πλήρη αλγόριθμο οι γραμμές Είσοδος:/Έξοδος: είναι ΥΠΟΧΡΕΩΤΙΚΕΣ
  // — πρέπει να ΥΠΑΡΧΕΙ η γραμμή ΚΑΙ (βλ. έλεγχο `missingInputs`/
  // `missingOutputs` παρακάτω, μετά την αναγνώριση του σώματος) να αναφέρει
  // κάθε μεταβλητή που όντως διαβάζεται/τυπώνεται σε κάποιο Βήμα. Μια
  // μεταβλητή της Είσοδος:/Έξοδος: που ΔΕΝ διαβάζεται/τυπώνεται ρητά σε Βήμα
  // παραμένει έμμεση Δεδομένα/Αποτελέσματα (βλ. `collectReadWrittenVarNames`
  // παρακάτω, §14.1 "Παρατηρήσεις"). Μόνο σε "Τμήμα αλγορίθμου" λείπουν
  // εντελώς οι γραμμές (βλ. `parseNaturalLanguageStepsToStatements`, που δεν
  // τις αναμένει καν).
  if (!sawInput) return { ok: false, reason: 'Λείπει η γραμμή "Είσοδος:" (υποχρεωτική σε πλήρη αλγόριθμο).' };
  if (!sawOutput) return { ok: false, reason: 'Λείπει η γραμμή "Έξοδος:" (υποχρεωτική σε πλήρη αλγόριθμο).' };
  const stepsResult = parseStepLinesOnly(stepLines);
  if (!stepsResult.ok) return stepsResult;
  return { ok: true, parsed: { name, inputVars, outputVars, ...stepsResult.parsed } };
}

// --- Βήμα 2: IR → δομημένο AST (αναδρομική αναγνώριση σχημάτων) --------------

const POS = { line: 1, column: 1 };

function isIdentifierAssignment(stmt: Statement): stmt is Statement & { kind: 'AssignmentStatement'; target: { kind: 'Identifier'; name: string } } {
  return stmt.kind === 'AssignmentStatement' && stmt.target.kind === 'Identifier';
}

function isSimpleVarComparison(expr: Expression, varName: string): expr is Expression & { kind: 'BinaryExpression'; operator: '<=' | '>='; left: { kind: 'Identifier' }; right: Expression } {
  return (
    expr.kind === 'BinaryExpression' &&
    (expr.operator === '<=' || expr.operator === '>=') &&
    expr.left.kind === 'Identifier' &&
    expr.left.name === varName
  );
}

function recognizeSequence(steps: ReadonlyMap<number, IRStep>, start: number, end: number): Statement[] | null {
  if (start > end) return null;
  const result: Statement[] = [];
  let pos = start;
  while (pos < end) {
    const match = recognizeOne(steps, pos, end);
    if (match === null) return null;
    result.push(match.statement);
    pos = match.nextStep;
    if (pos > end) return null;
  }
  return pos === end ? result : null;
}

function recognizeOne(steps: ReadonlyMap<number, IRStep>, pos: number, end: number): { readonly statement: Statement; readonly nextStep: number } | null {
  // ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ: ψάχνει το ΠΡΩΤΟ condgoto2 μέσα στο [pos+1, end) που κλείνει πίσω στο pos.
  for (let m = pos + 1; m < end; m++) {
    const s = steps.get(m);
    if (s !== undefined && s.kind === 'condgoto2' && s.falseTarget === pos) {
      const body = recognizeSequence(steps, pos, m);
      if (body !== null) {
        return { statement: { kind: 'RepeatUntilStatement', body, condition: s.condition, ...POS }, nextStep: m + 1 };
      }
      break;
    }
  }

  const first = steps.get(pos);
  if (first === undefined) return null;

  // ΓΙΑ: "Θέσε V = από" ακολουθούμενο αμέσως από έλεγχο πάνω στο ίδιο V.
  if (first.kind === 'plain' && isIdentifierAssignment(first.statement)) {
    const varName = first.statement.target.name;
    const condStep = steps.get(pos + 1);
    if (condStep !== undefined && condStep.kind === 'condgoto2' && isSimpleVarComparison(condStep.condition, varName)) {
      const forMatch = tryMatchFor(steps, pos, first.statement.value, varName, condStep);
      if (forMatch !== null) return forMatch;
    }
  }

  // condgoto2 στο pos, με trueTarget αμέσως μετά: ΟΣΟ ή ΑΝ...ΑΛΛΙΩΣ (C=1).
  if (first.kind === 'condgoto2' && first.trueTarget === pos + 1) {
    const closing = steps.get(first.falseTarget - 1);
    if (closing !== undefined && closing.kind === 'goto') {
      if (closing.target === pos) {
        const body = recognizeSequence(steps, pos + 1, first.falseTarget - 1);
        if (body !== null) {
          return { statement: { kind: 'WhileStatement', condition: first.condition, body, endLine: null, ...POS }, nextStep: first.falseTarget };
        }
      } else {
        const rangeEnd = closing.target;
        const thenBody = recognizeSequence(steps, pos + 1, first.falseTarget - 1);
        const elseBody = thenBody !== null ? recognizeSequence(steps, first.falseTarget, rangeEnd) : null;
        if (thenBody !== null && elseBody !== null) {
          return {
            statement: { kind: 'IfStatement', condition: first.condition, thenBranch: thenBody, elseIfBranches: [], elseBranch: elseBody, endLine: null, ...POS },
            nextStep: rangeEnd,
          };
        }
      }
    }
  }

  // condgoto1 στο pos: ΑΝ χωρίς ΑΛΛΙΩΣ, ή αλυσίδα ΑΝ/ΑΛΛΙΩΣ_ΑΝ/[ΑΛΛΙΩΣ] (C>1).
  if (first.kind === 'condgoto1') {
    const chainMatch = tryMatchIfChain(steps, pos, end);
    if (chainMatch !== null) return chainMatch;
  }

  if (first.kind === 'plain') return { statement: first.statement, nextStep: pos + 1 };

  return null;
}

/** §14.1 ερ.7: "Θέσε V=από" + "Αν V<=/>=μέχρι τότε...αλλιώς..." + σώμα + "Θέσε V=V+βήμα" + "Πήγαινε πίσω στον έλεγχο". */
function tryMatchFor(
  steps: ReadonlyMap<number, IRStep>,
  K: number,
  fromExpr: Expression,
  varName: string,
  condStep: CondGoto2Step,
): { readonly statement: Statement; readonly nextStep: number } | null {
  if (condStep.condition.kind !== 'BinaryExpression') return null;
  const bodyStart = K + 2;
  if (condStep.trueTarget !== bodyStart) return null;
  const rangeEnd = condStep.falseTarget;
  if (rangeEnd - 2 < bodyStart) return null;
  const incStep = steps.get(rangeEnd - 2);
  const backGoto = steps.get(rangeEnd - 1);
  if (incStep === undefined || incStep.kind !== 'plain' || !isIdentifierAssignment(incStep.statement)) return null;
  if (incStep.statement.target.name !== varName) return null;
  const incValue = incStep.statement.value;
  if (incValue.kind !== 'BinaryExpression' || incValue.operator !== '+') return null;
  if (incValue.left.kind !== 'Identifier' || incValue.left.name !== varName) return null;
  if (backGoto === undefined || backGoto.kind !== 'goto' || backGoto.target !== K + 1) return null;
  const body = recognizeSequence(steps, bodyStart, rangeEnd - 2);
  if (body === null) return null;

  const stepExpr = incValue.right;
  const step = stepExpr.kind === 'NumberLiteral' && stepExpr.value === 1 ? null : stepExpr;
  return {
    statement: {
      kind: 'ForStatement',
      variable: incStep.statement.target,
      from: fromExpr,
      to: condStep.condition.right,
      step,
      body,
      endLine: null,
      ...POS,
    },
    nextStep: rangeEnd,
  };
}

/** §14.1 ερ.2/ερ.4: 1 ή περισσότερες συνεχόμενες condgoto1 στο pos. */
function tryMatchIfChain(steps: ReadonlyMap<number, IRStep>, pos: number, end: number): { readonly statement: Statement; readonly nextStep: number } | null {
  let branchCount = 0;
  while (steps.get(pos + branchCount)?.kind === 'condgoto1') branchCount++;
  if (branchCount === 0) return null;

  const condSteps: CondGoto1Step[] = [];
  for (let i = 0; i < branchCount; i++) condSteps.push(steps.get(pos + i) as CondGoto1Step);
  const branchStarts = condSteps.map((s) => s.trueTarget);
  const fallthroughStart = pos + branchCount;

  if (branchCount === 1) {
    const skip = steps.get(fallthroughStart);
    if (skip === undefined || skip.kind !== 'goto') return null;
    if (branchStarts[0] !== fallthroughStart + 1) return null;
    const rangeEnd = skip.target;
    const body = recognizeSequence(steps, branchStarts[0]!, rangeEnd);
    if (body === null) return null;
    return {
      statement: { kind: 'IfStatement', condition: condSteps[0]!.condition, thenBranch: body, elseIfBranches: [], elseBranch: null, endLine: null, ...POS },
      nextStep: rangeEnd,
    };
  }

  const firstBranchStart = branchStarts[0]!;
  let hasElse: boolean;
  let elseBody: Statement[] = [];
  let rangeEnd: number;
  if (firstBranchStart === fallthroughStart + 1) {
    const skip = steps.get(fallthroughStart);
    if (skip === undefined || skip.kind !== 'goto') return null;
    hasElse = false;
    rangeEnd = skip.target;
  } else {
    const closing = steps.get(firstBranchStart - 1);
    if (closing === undefined || closing.kind !== 'goto') return null;
    const body = recognizeSequence(steps, fallthroughStart, firstBranchStart - 1);
    if (body === null) return null;
    elseBody = body;
    hasElse = true;
    rangeEnd = closing.target;
  }

  const bodies: Statement[][] = [];
  for (let i = 0; i < branchCount; i++) {
    const isLastBranch = i === branchCount - 1;
    const boundary = isLastBranch ? rangeEnd : branchStarts[i + 1]!;
    const closing = steps.get(boundary - 1);
    const hasExplicitSkip = closing !== undefined && closing.kind === 'goto' && closing.target === rangeEnd;
    // Το ΤΕΛΕΥΤΑΙΟ κλαδί ήδη καταλήγει φυσικά στο rangeEnd (είναι το boundary
    // του) — το ρητό "Πήγαινε στο Βήμα <rangeEnd>" στο τέλος του είναι
    // περιττό και συχνά παραλείπεται. Τα ενδιάμεσα κλάδια ΔΕΝ μπορούν να το
    // παραλείψουν (χρειάζονται το άλμα για να προσπεράσουν τα επόμενα κλάδια).
    if (!isLastBranch && !hasExplicitSkip) return null;
    const body = recognizeSequence(steps, branchStarts[i]!, hasExplicitSkip ? boundary - 1 : boundary);
    if (body === null) return null;
    bodies.push(body);
  }

  const [firstBody, ...restBodies] = bodies;
  return {
    statement: {
      kind: 'IfStatement',
      condition: condSteps[0]!.condition,
      thenBranch: firstBody!,
      elseIfBranches: condSteps.slice(1).map((c, i) => ({ condition: c.condition, body: restBodies[i]! })),
      elseBranch: hasElse ? elseBody : null,
      endLine: null,
      ...POS,
    },
    nextStep: rangeEnd,
  };
}

/**
 * §14.1, "Παρατηρήσεις" (βλ. στιγμιότυπο βιβλίου): μια μεταβλητή της
 * Είσοδος:/Έξοδος: θεωρείται "Δεδομένα"/"Αποτελέσματα" ΜΟΝΟ όταν ΔΕΝ
 * διαβάζεται/εμφανίζεται ρητά μέσα στα Βήματα — το Διάβασε/Γράψε είναι ο
 * "αυτοτελής" τρόπος να μπει/βγει μια τιμή, το Δεδομένα/Αποτελέσματα ο
 * "σιωπηλός" (ισοδύναμος με τον αυτόματο prompt/print του interpreter,
 * βλ. `promptForDataVariables`/`printResultVariables`). Αν μια μεταβλητή
 * ΚΑΙ διαβάζεται ρητά ΚΑΙ εμφανίζεται στο Δεδομένα, ο interpreter θα τη
 * ζητούσε ΔΙΠΛΑ (μία φορά αυτόματα από το Δεδομένα, ξανά από το Διάβασε) —
 * γι' αυτό αφαιρούνται εδώ οι ήδη-καλυμμένες μεταβλητές.
 */
function collectReadWrittenVarNames(statements: readonly Statement[]): { readonly read: ReadonlySet<string>; readonly written: ReadonlySet<string> } {
  const read = new Set<string>();
  const written = new Set<string>();

  const visitAll = (stmts: readonly Statement[]): void => {
    for (const stmt of stmts) visitOne(stmt);
  };
  const visitOne = (stmt: Statement): void => {
    switch (stmt.kind) {
      case 'ReadStatement':
        for (const target of stmt.targets) if (target.kind === 'Identifier') read.add(target.name);
        break;
      case 'WriteStatement':
        for (const value of stmt.values) if (value.kind === 'Identifier') written.add(value.name);
        break;
      case 'IfStatement':
        visitAll(stmt.thenBranch);
        for (const branch of stmt.elseIfBranches) visitAll(branch.body);
        if (stmt.elseBranch !== null) visitAll(stmt.elseBranch);
        break;
      case 'ForStatement':
      case 'WhileStatement':
      case 'RepeatUntilStatement':
        visitAll(stmt.body);
        break;
      case 'SwitchStatement':
        for (const switchCase of stmt.cases) visitAll(switchCase.body);
        if (stmt.elseBody !== null) visitAll(stmt.elseBody);
        break;
      default:
        break;
    }
  };

  visitAll(statements);
  return { read, written };
}

export type NaturalLanguageParseResult = { readonly ok: true; readonly program: PseudoglossaProgram } | { readonly ok: false; readonly reason: string };

/**
 * Δέχεται κείμενο "φυσικής γλώσσας κατά βήματα" (§14.1) και προσπαθεί να το
 * ξαναχτίσει σε δομημένο πρόγραμμα Ψευδογλώσσας. Αρνείται (`ok: false`) αντί
 * να μαντέψει όταν κάτι δεν ταιριάζει ΑΚΡΙΒΩΣ σε ένα από τα 6 γνωστά σχήματα.
 */
export function parseNaturalLanguageStepsToProgram(text: string): NaturalLanguageParseResult {
  const header = parseHeaderAndSteps(text);
  if (!header.ok) return header;
  const { name, inputVars, outputVars, steps, maxStep } = header.parsed;
  const body = maxStep === 0 ? [] : recognizeSequence(steps, 1, maxStep + 1);
  if (body === null) {
    return {
      ok: false,
      reason: 'Δεν αναγνωρίζονται όλα τα βήματα ως έγκυρες δομές (ΑΝ/ΟΣΟ/ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ/ΓΙΑ) — έλεγξε τους αριθμούς Βημάτων και τα "Πήγαινε στο Βήμα".',
    };
  }
  const { read, written } = collectReadWrittenVarNames(body);
  // Κάθε μεταβλητή που πράγματι διαβάζεται/τυπώνεται σε κάποιο Βήμα ΠΡΕΠΕΙ να
  // αναφέρεται στην Είσοδος:/Έξοδος: — δεν αρκεί μόνο να ΥΠΑΡΧΕΙ η γραμμή
  // (βλ. πιο πάνω), πρέπει να ΠΕΡΙΕΧΕΙ πράγματι τα ονόματα. Χωρίς αυτό, ένας
  // πλήρης αλγόριθμος με άδεια κεφαλίδα θα "έτρεχε" κανονικά παρόλο που δεν
  // δηλώνει καθόλου τα δεδομένα/αποτελέσματά του.
  const missingInputs = [...read].filter((v) => !inputVars.includes(v));
  if (missingInputs.length > 0) {
    return {
      ok: false,
      reason: `Η "Είσοδος:" πρέπει να αναφέρει τις μεταβλητές που διαβάζονται: λείπει ${missingInputs.join(', ')}.`,
    };
  }
  const missingOutputs = [...written].filter((v) => !outputVars.includes(v));
  if (missingOutputs.length > 0) {
    return {
      ok: false,
      reason: `Η "Έξοδος:" πρέπει να αναφέρει τις μεταβλητές που τυπώνονται: λείπει ${missingOutputs.join(', ')}.`,
    };
  }
  const dataVariables = inputVars.filter((v) => !read.has(v));
  const resultVariables = outputVars.filter((v) => !written.has(v));
  return {
    ok: true,
    program: { kind: 'Program', dialect: Dialect.PSEUDOGLOSSA, name, dataVariables, resultVariables, body, endLine: null, line: 1, column: 1 },
  };
}

export type NaturalLanguageStatementsParseResult = { readonly ok: true; readonly statements: readonly Statement[] } | { readonly ok: false; readonly reason: string };

/**
 * Ίδιο με `parseNaturalLanguageStepsToProgram`, αλλά χωρίς κεφαλίδα — για
 * "Τμήμα αλγορίθμου" (βλ. `parseFragment` στο main.ts): μόνο "Βήμα Ν: ..."
 * γραμμές, καμία "Αλγόριθμος"/"Είσοδος:"/"Έξοδος:" αναμένεται ή επιτρέπεται.
 */
export function parseNaturalLanguageStepsToStatements(text: string): NaturalLanguageStatementsParseResult {
  const lines = text
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  const stepsResult = parseStepLinesOnly(lines);
  if (!stepsResult.ok) return stepsResult;
  const { steps, maxStep } = stepsResult.parsed;
  const statements = maxStep === 0 ? [] : recognizeSequence(steps, 1, maxStep + 1);
  if (statements === null) {
    return {
      ok: false,
      reason: 'Δεν αναγνωρίζονται όλα τα βήματα ως έγκυρες δομές (ΑΝ/ΟΣΟ/ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ/ΓΙΑ) — έλεγξε τους αριθμούς Βημάτων και τα "Πήγαινε στο Βήμα".',
    };
  }
  return { ok: true, statements };
}
